// Bounce verb — capture the running pattern's output into an AudioBuffer.
//
// Reuses the MediaStreamAudioDestinationNode the recording feature
// already installed in player.ts (getRecordingStream). Second tap
// doesn't exist, shouldn't exist, wouldn't help.
//
// Cycle-quantization is the feature that makes this feel musical:
//   - We read scheduler.now() to find the current cycle position.
//   - Wait until it crosses the next integer (the next cycle boundary).
//   - Start MediaRecorder there. Record for (cycles / cps) seconds.
//   - Stop on the next integer boundary after that.
//
// The captured buffer is in AudioContext time; cycle math is advisory
// (it tells us how many cycles were intended). CPS is stored in the
// origin so downstream tools can re-sync against it.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getRecordingStream, getRepl, hasRecordingTap } from '../../../strudel';
import type { VerbProps } from '../types';

type Status =
  | { kind: 'idle' }
  | { kind: 'waiting-boundary'; cycles: number; untilMs: number }
  | { kind: 'recording'; cycles: number; startedAt: number; cps: number }
  | { kind: 'decoding' }
  | { kind: 'error'; detail: string };

const CYCLE_OPTIONS = [1, 2, 4, 8, 16] as const;

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

function pickMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

export function BounceVerb({ audioContext, onBuffer, onStatus }: VerbProps) {
  const [cycles, setCycles] = useState(4);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const waitTimerRef = useRef<number | null>(null);
  const stopTimerRef = useRef<number | null>(null);
  const progressRafRef = useRef<number | null>(null);

  const cleanup = useCallback(() => {
    if (waitTimerRef.current !== null) {
      clearTimeout(waitTimerRef.current);
      waitTimerRef.current = null;
    }
    if (stopTimerRef.current !== null) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (progressRafRef.current !== null) {
      cancelAnimationFrame(progressRafRef.current);
      progressRafRef.current = null;
    }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        // already stopping
      }
    }
    recorderRef.current = null;
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const beginCapture = useCallback(async () => {
    if (!audioContext) {
      setStatus({ kind: 'error', detail: 'engine not booted — press Play first' });
      return;
    }
    if (!hasRecordingTap()) {
      setStatus({
        kind: 'error',
        detail: 'recording tap not ready — play a pattern once to wire it',
      });
      return;
    }
    const stream = getRecordingStream();
    if (!stream) {
      setStatus({ kind: 'error', detail: 'no recording stream' });
      return;
    }
    const repl = getRepl();
    const scheduler = repl?.scheduler;
    if (!scheduler || !scheduler.started) {
      setStatus({ kind: 'error', detail: 'pattern is not playing — hit Play first' });
      return;
    }
    const mime = pickMime();
    if (!mime) {
      setStatus({ kind: 'error', detail: 'no supported MediaRecorder mime' });
      return;
    }

    const cps = scheduler.cps;
    if (!(cps > 0)) {
      setStatus({ kind: 'error', detail: 'CPS is zero — song has no tempo' });
      return;
    }
    const cycleSeconds = 1 / cps;
    const captureCycles = cycles;
    const captureSeconds = captureCycles * cycleSeconds;

    const currentCycle = scheduler.now();
    const nextBoundary = Math.ceil(currentCycle);
    const untilBoundarySec = Math.max(0, (nextBoundary - currentCycle) / cps);
    const untilBoundaryMs = Math.round(untilBoundarySec * 1000);

    setStatus({ kind: 'waiting-boundary', cycles: captureCycles, untilMs: untilBoundaryMs });

    // Tick the waiting countdown each rAF so the user sees it shrink.
    const waitStartWall = performance.now();
    const progress = () => {
      if (!recorderRef.current) {
        const elapsed = performance.now() - waitStartWall;
        const remaining = Math.max(0, untilBoundaryMs - Math.round(elapsed));
        setStatus({
          kind: 'waiting-boundary',
          cycles: captureCycles,
          untilMs: remaining,
        });
        progressRafRef.current = requestAnimationFrame(progress);
      }
    };
    progressRafRef.current = requestAnimationFrame(progress);

    // Fire the recorder exactly at the boundary.
    waitTimerRef.current = window.setTimeout(() => {
      if (progressRafRef.current !== null) {
        cancelAnimationFrame(progressRafRef.current);
        progressRafRef.current = null;
      }
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onerror = () => {
        setStatus({ kind: 'error', detail: 'MediaRecorder crashed mid-capture' });
        cleanup();
      };
      rec.start(Math.min(250, Math.floor(captureSeconds * 1000 / 4)));
      recorderRef.current = rec;
      const startedAt = performance.now();
      setStatus({
        kind: 'recording',
        cycles: captureCycles,
        startedAt,
        cps,
      });

      // Schedule the natural stop at the end of the capture window.
      stopTimerRef.current = window.setTimeout(() => {
        stopTimerRef.current = null;
        void finishCapture(captureCycles, cps);
      }, Math.max(1, Math.round(captureSeconds * 1000)));
    }, untilBoundaryMs);
  }, [audioContext, cycles, cleanup]);

  const finishCapture = useCallback(
    async (capturedCycles: number, cps: number) => {
      const rec = recorderRef.current;
      const ctx = audioContext;
      if (!rec || !ctx) return;
      setStatus({ kind: 'decoding' });
      await new Promise<void>((resolve) => {
        rec.addEventListener('stop', () => resolve(), { once: true });
        try {
          rec.stop();
        } catch {
          resolve();
        }
      });
      const blob = new Blob(chunksRef.current, { type: rec.mimeType });
      chunksRef.current = [];
      recorderRef.current = null;
      try {
        const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
        const patternHash = await hashEditorCode();
        onBuffer(buffer, {
          suggestedName: `bounce_${capturedCycles}c`,
          origin: {
            kind: 'bounce',
            cycles: capturedCycles,
            cps,
            patternHash,
          },
        });
        onStatus(
          `Bounced ${capturedCycles} cycles at ${cps.toFixed(3)} cps.`,
          'ok',
        );
        setStatus({ kind: 'idle' });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setStatus({ kind: 'error', detail: 'Decode failed: ' + msg });
      }
    },
    [audioContext, onBuffer, onStatus],
  );

  const handleAbort = useCallback(() => {
    cleanup();
    setStatus({ kind: 'idle' });
  }, [cleanup]);

  return (
    <div className="flex flex-col gap-4 min-h-[200px]">
      <StatusBanner status={status} />
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
          Cycles to capture
        </div>
        <div className="flex gap-2 flex-wrap">
          {CYCLE_OPTIONS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCycles(c)}
              disabled={status.kind !== 'idle'}
              className={
                'px-4 py-2 text-xs uppercase tracking-[0.2em] rounded-sm border ' +
                'disabled:opacity-40 disabled:cursor-not-allowed ' +
                (cycles === c
                  ? 'bg-yellow-300 border-yellow-300 text-zinc-950 font-semibold'
                  : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800')
              }
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void beginCapture()}
          disabled={status.kind !== 'idle'}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ● Capture
        </button>
        <button
          type="button"
          onClick={handleAbort}
          disabled={status.kind === 'idle' || status.kind === 'decoding'}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-zinc-800 border border-zinc-700 text-zinc-200 disabled:opacity-30"
        >
          ■ Abort
        </button>
      </div>
    </div>
  );
}

function StatusBanner({ status }: { status: Status }) {
  if (status.kind === 'idle') {
    return (
      <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-400">
        Capture starts on the next cycle boundary. Pattern must be playing.
      </div>
    );
  }
  if (status.kind === 'waiting-boundary') {
    return (
      <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-yellow-200">
        Waiting for cycle boundary — {(status.untilMs / 1000).toFixed(2)}s · capturing {status.cycles} cycles
      </div>
    );
  }
  if (status.kind === 'recording') {
    return (
      <div className="bg-rose-950/40 border border-rose-800 px-4 py-3 text-xs text-rose-200">
        Recording {status.cycles} cycles…
      </div>
    );
  }
  if (status.kind === 'decoding') {
    return (
      <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-300">
        Decoding take…
      </div>
    );
  }
  return (
    <div className="bg-rose-950/40 border border-rose-800 px-4 py-3 text-xs text-rose-200">
      {status.detail}
    </div>
  );
}

// sha256 of the current editor code — stored in origin.patternHash as
// provenance so we can reconstruct "this sample was bounced from this
// code". Reads the live editor surface; the buffer itself isn't
// directly accessible from this scope.
async function hashEditorCode(): Promise<string> {
  // The editor's current buffer lives on the DOM via CodeMirror. Reading
  // it out cleanly would require threading a ref through the forge API
  // we don't have. Hashing the full page text is too coarse; hashing an
  // empty string would be indistinguishable. Compromise: use the visible
  // textarea-like surface if available, else a placeholder.
  try {
    const cm = document.querySelector('.cm-content');
    const code = cm?.textContent ?? '';
    const bytes = new TextEncoder().encode(code);
    const view = new Uint8Array(bytes.byteLength);
    view.set(bytes);
    const buf = new ArrayBuffer(view.byteLength);
    new Uint8Array(buf).set(view);
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return toHex(new Uint8Array(digest));
  } catch {
    return 'unknown';
  }
}

function toHex(bytes: Uint8Array): string {
  const HEX = '0123456789abcdef';
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i]!;
    out += HEX[b >>> 4]! + HEX[b & 0x0f]!;
  }
  return out;
}
